
import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ProfileFormValues } from "@/hooks/profile";
import UniversityLabSelect from "./university/UniversityLabSelect";
import CountrySelector from "@/components/ui/CountrySelector";

interface PersonalInfoFormProps {
  form: UseFormReturn<ProfileFormValues>;
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({ form }) => {
  // Helper function to format URL inputs correctly
  const handleUrlInput = (event: React.ChangeEvent<HTMLInputElement>, field: any) => {
    let value = event.target.value;
    
    // Don't modify empty values
    if (!value.trim()) {
      field.onChange(value);
      return;
    }
    
    // Only add prefix if no protocol is present
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      value = `https://${value}`;
    }
    
    field.onChange(value);
  };
  
  return (
    <>
      <div className="grid md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Your email address" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display_username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Name</FormLabel>
              <FormControl>
                <Input placeholder="Name shown to other users" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      {/* University and Lab selection */}
      <UniversityLabSelect form={form} />
      
      <div className="grid md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input placeholder="Your city" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <FormControl>
                <CountrySelector
                  value={field.value || ""}
                  onValueChange={(value) => field.onChange(value)}
                  placeholder="Select your country"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expertise"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expertise</FormLabel>
              <FormControl>
                <Input placeholder="Your area of expertise" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="linkedin_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LinkedIn Profile</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Your LinkedIn profile URL" 
                  value={field.value || ""}
                  onChange={(e) => handleUrlInput(e, field)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Your personal or professional website" 
                  value={field.value || ""}
                  onChange={(e) => handleUrlInput(e, field)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={form.control}
        name="research_interests"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Research Interests</FormLabel>
            <FormControl>
              <Textarea 
                placeholder="Describe your research interests" 
                {...field} 
                value={field.value || ""}
                rows={4}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default PersonalInfoForm;
